/**
 * Proof-of-funds reviewer queue (admin/employee only — the route is gated on
 * isReviewer and the backend enforces the real check on every call).
 *
 * Workflow mirrors the service contract (docs/PLATFORM.md):
 *   pending → claim (self-claim forbidden) → under_review → approve | reject.
 * Approval is the ONLY path that upgrades a member to accredited-investor.
 * Submissions are financial PII — the backend audit-logs every queue read,
 * detail read, claim, and decision.
 */
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import * as pofApi from '../api/proofOfFunds';
import * as filesApi from '../api/files';
import { usePagedList } from '../lib/usePagedList';
import { useToast } from '../components/toast';
import Tabs from '../components/Tabs';
import { ErrorAlert, Spinner, EmptyState, PagedListFooter, StatusPill, formatDate } from '../components/ui';
import { ui, cn } from '../lib/ui';

const STATUSES = ['pending', 'under_review', 'verified', 'rejected', 'expired'];
const TABS = STATUSES.map((s) => ({ id: s, label: s.replace(/_/g, ' ') }));
const PAGE_SIZE = 25;

export default function ReviewQueue() {
  const { user } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState('pending');
  const [expanded, setExpanded] = useState(null); // proofId of the open row

  const list = usePagedList(
    (cursor) =>
      pofApi
        .listQueue({ status, limit: PAGE_SIZE, lastEvaluatedKey: cursor })
        .then((d) => ({ items: d.proofs || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [status]
  );

  return (
    <>
      <h1 className={ui.h1}>Accreditation review queue</h1>
      <p className={cn(ui.muted, 'text-body-s')}>
        Submissions contain confidential financial information — every read and
        decision here is audit-logged. Approving a submission upgrades the
        member to accredited investor.
      </p>

      <div className="mt-4">
        <Tabs
          tabs={TABS}
          value={status}
          onChange={(id) => {
            setStatus(id);
            setExpanded(null);
          }}
          idPrefix="review-status"
          aria-label="Filter by status"
        />
      </div>

      <div role="tabpanel" id="review-status-panel" aria-labelledby={`review-status-${status}`}>
        <ErrorAlert error={list.error} />

        {list.loading ? (
          <Spinner />
        ) : list.items.length === 0 ? (
          !list.error && <EmptyState>No {status.replace(/_/g, ' ')} submissions.</EmptyState>
        ) : (
          <div className="mt-4">
            {list.items.map((proof) => (
              <ReviewCard
                key={proof.proofId}
                proof={proof}
                reviewerId={user && user.userId}
                open={expanded === proof.proofId}
                onToggle={() =>
                  setExpanded((current) => (current === proof.proofId ? null : proof.proofId))
                }
                // Any claim/approve/reject moves the proof out of the
                // currently filtered status bucket, so drop it from view
                // rather than refetching the whole page.
                onChanged={() => list.removeItem(proof.proofId, 'proofId')}
                toast={toast}
              />
            ))}
          </div>
        )}

        <PagedListFooter
          hasMore={list.hasMore}
          loadingMore={list.loadingMore}
          onLoadMore={list.loadMore}
          endLabel={list.items.length ? 'No more submissions.' : null}
        />
      </div>
    </>
  );
}

function ReviewCard({ proof, reviewerId, open, onToggle, onChanged, toast }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null); // 'approve' | 'reject'
  const [notes, setNotes] = useState('');

  const isOwn = proof.userId === reviewerId;
  const claimable = proof.status === 'pending' && !isOwn;
  const decidable = proof.status === 'under_review' && !isOwn;

  async function claim() {
    setError(null);
    setBusy(true);
    try {
      await pofApi.claimProof(proof.proofId);
      toast.success('Submission claimed for review.');
      onChanged();
    } catch (err) {
      setError(err);
      toast.error(err);
      setBusy(false);
    }
  }

  // proofId/decisionValue are explicit arguments (not read from state) so a
  // fast double-click can never fire the decision the *previous* click set
  // up (the FEA-14 fix) — the setDecision call below is display-only.
  async function submitDecision(proofId, decisionValue) {
    setDecision(decisionValue);
    setError(null);
    setBusy(true);
    try {
      await pofApi.reviewProof(proofId, {
        decision: decisionValue,
        reviewNotes: notes || undefined
      });
      toast.success(decisionValue === 'approve' ? 'Submission approved.' : 'Submission rejected.');
      onChanged();
    } catch (err) {
      setError(err);
      toast.error(err);
      setBusy(false);
    }
  }

  async function openDocument(fileId) {
    setError(null);
    try {
      // May be rejected while malware scanning has not cleared the upload
      // (fail-closed backend policy) — surface the API's error as-is.
      const { downloadUrl } = await filesApi.getDownloadUrl(fileId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err);
      toast.error(err);
    }
  }

  return (
    <section className={cn(ui.card, 'mt-4')}>
      <div className={cn(ui.row, ui.spread, 'items-baseline')}>
        <h3 className="m-0 text-title-s">
          {proof.declaredAmount} {proof.currency}
          {isOwn && <span className={ui.pillNeutral}> your own submission</span>}
        </h3>
        <StatusPill status={proof.status} />
      </div>
      <p className={cn(ui.muted, 'text-body-s')}>
        Member {proof.userId} — submitted {formatDate(proof.submittedAt)}
        {proof.source ? ` — source: ${proof.source}` : ''}
        {proof.reviewedBy ? ` — reviewer: ${proof.reviewedBy}` : ''}
      </p>

      <ErrorAlert error={error} />

      <div className={ui.row}>
        <button className={ui.btnSecondary} onClick={onToggle}>
          {open ? 'Hide details' : 'View details'}
        </button>
        {claimable && (
          <button className={ui.btnPrimary} onClick={claim} disabled={busy}>
            {busy ? 'Claiming…' : 'Claim for review'}
          </button>
        )}
      </div>

      {open && (
        <div className="mt-4">
          <h4>Documents</h4>
          {(proof.documents || []).length === 0 ? (
            <EmptyState>No documents attached.</EmptyState>
          ) : (
            <ul>
              {(proof.documents || []).map((doc) => (
                <li key={doc.fileId}>
                  <button
                    type="button"
                    className={ui.btnSecondary}
                    onClick={() => openDocument(doc.fileId)}
                  >
                    Open {doc.fileName || doc.fileId}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {proof.notes && (
            <p className="text-body-s">
              <strong>Member notes:</strong> {proof.notes}
            </p>
          )}
          {proof.reviewNotes && (
            <p className="text-body-s">
              <strong>Review notes:</strong> {proof.reviewNotes}
            </p>
          )}
          {proof.expiresAt && (
            <p className={cn(ui.muted, 'text-body-s')}>Verification expires {formatDate(proof.expiresAt)}.</p>
          )}

          {decidable && (
            <form onSubmit={(e) => e.preventDefault()} className="mt-4">
              <label className={ui.field}>
                <span className={ui.label}>Review notes (kept on the record)</span>
                <textarea
                  className={ui.input}
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
              <div className={ui.row}>
                <button
                  className={ui.btnPrimary}
                  type="button"
                  disabled={busy}
                  onClick={() => submitDecision(proof.proofId, 'approve')}
                >
                  {busy && decision === 'approve' ? 'Approving…' : 'Approve — grant accredited status'}
                </button>
                <button
                  className={ui.btnDanger}
                  type="button"
                  disabled={busy}
                  onClick={() => submitDecision(proof.proofId, 'reject')}
                >
                  {busy && decision === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
